import { AnimatedComponent } from '@react-spring/web';
import React from 'react';

import { ExtendAndOverride } from '../../utils/ExtendAndOverride';
import { ScaleWithValueType } from '../../utils/SupportedScale';
import { GridLines, GridLinesProps } from './GridLines';

interface GridOwnProps<TX, TY> {
  /**
   * A d3 scale used for values along the x-axis.
   */
  xScale: ScaleWithValueType<TX>;
  /**
   * The set of x values for which to show grid lines.
   */
  xValues: TX[];
  /**
   * A d3 scale used for values along the y-axis.
   */
  yScale: ScaleWithValueType<TY>;
  /**
   * The set of y values for which to show grid lines.
   */
  yValues: TY[];
}

export type GridProps<TX, TY> = ExtendAndOverride<
  GridOwnProps<TX, TY>,
  Pick<GridLinesProps<never>, 'isAnimated'> & Omit<React.ComponentPropsWithoutRef<AnimatedComponent<'line'>>, 'css'>
>;

interface GridComponent {
  <TX, TY>(props: GridProps<TX, TY>): JSX.Element;
}

/**
 * Component to render an SVG grid, given positions and ranges for x- and y-axes.
 */
export const Grid = React.memo(
  <TX, TY>({ xScale, xValues, yScale, yValues, ...lineProps }: GridProps<TX, TY>): JSX.Element => {
    const [verticalLineYStart, verticalLineYEnd] = yScale.range();
    const [horizontalLineXStart, horizontalLineXEnd] = xScale.range();

    return (
      <>
        <GridLines
          {...lineProps}
          start={verticalLineYStart}
          end={verticalLineYEnd}
          scale={xScale}
          values={xValues}
          isVertical={false}
        />
        <GridLines
          {...lineProps}
          start={horizontalLineXStart}
          end={horizontalLineXEnd}
          scale={yScale}
          values={yValues}
          isVertical
        />
      </>
    );
  },
) as GridComponent;
